import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Storage helpers — read from sessionStorage first (unchecked-Remember-me),
// fall back to localStorage (checked or legacy). Writes go back to WHICHEVER
// storage currently holds the token so the user's choice is preserved.
const _getAuth = (key) => sessionStorage.getItem(key) || localStorage.getItem(key);
const _clearAuth = () => {
  ["token", "refresh_token"].forEach((k) => {
    sessionStorage.removeItem(k);
    localStorage.removeItem(k);
  });
  localStorage.removeItem("selectedUnit");
};
const _writeAuth = (key, val) => {
  // Write to the storage that already holds this key; default to localStorage.
  if (sessionStorage.getItem(key) !== null) sessionStorage.setItem(key, val);
  else localStorage.setItem(key, val);
};

// Add token to requests
api.interceptors.request.use((config) => {
  const token = _getAuth("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors — attempt silent refresh before logging out
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  failedQueue = [];
};

// Gateway/origin failures (Cloudflare 5xx pages, HTML error bodies, empty or
// dropped responses) must NEVER surface their raw text in the UI. Pages toast
// `error.response?.data?.detail`, so we rewrite `data` to a friendly message
// here — one place, covers every screen. Real API errors (JSON with a
// `detail`) are never touched.
const GATEWAY_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524, 525, 526]);
const FRIENDLY_GATEWAY_MSG =
  "Server connection interrupted — your last action may or may not have been saved. " +
  "Please refresh the page and check before re-entering.";

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Normalize gateway / dropped-connection errors before any page sees them.
    const _resp = error.response;
    if (_resp) {
      const _d = _resp.data;
      const _hasDetail = _d && typeof _d === "object" && _d.detail;
      const _looksRaw = typeof _d === "string" &&
        /<html|<!doctype|cloudflare|bad gateway|gateway time-?out/i.test(_d);
      if (!_hasDetail && (GATEWAY_STATUSES.has(_resp.status) || _looksRaw)) {
        _resp.data = { detail: FRIENDLY_GATEWAY_MSG };
      }
    } else if (error.request) {
      // No response at all — network drop, origin closed the connection, or
      // timeout. Synthesize a response-shaped error so every page's
      // `error.response?.data?.detail` shows the friendly text instead of
      // nothing / a raw browser error.
      error.response = { status: 0, data: { detail: FRIENDLY_GATEWAY_MSG } };
    }

    // Surface 403 forbidden as a global toast (Permission denied) — EXCEPT for
    // background probes marked { silent403: true } (optional enrichments like
    // store balances or the approvals badge, where "no permission" is a normal
    // outcome, not an error the user should see on every page load).
    if (error.response?.status === 403) {
      if (!originalRequest?.silent403) {
        const detail = error.response?.data?.detail || "Permission denied";
        toast.error(typeof detail === "string" ? detail : "Permission denied");
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry refresh or login calls
      if (originalRequest.url?.includes("/auth/refresh") || originalRequest.url?.includes("/auth/login")) {
        _clearAuth();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = _getAuth("refresh_token");
      if (!refreshToken) {
        isRefreshing = false;
        _clearAuth();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: refreshToken });
        const newToken = data.access_token;
        _writeAuth("token", newToken);
        // Persist the rotated refresh token under the SAME key the app already reads
        // ("refresh_token", snake_case). Without this write the server rotates but the
        // client keeps the old token, so the sliding window never actually extends.
        if (data.refresh_token) _writeAuth("refresh_token", data.refresh_token);
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        _clearAuth();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);


/* ---- PERF: short-lived GET cache (frontend only) ----
   Identical repeat GETs within 25s are served from memory instead of hitting
   the network again (e.g. Inventory -> Dashboard -> Inventory). ANY mutation
   (POST/PUT/PATCH/DELETE) clears the whole cache, so a save is always
   followed by fresh reads — stale-after-save is impossible. Blob/export and
   auth endpoints are never cached. Served data is deep-cloned so in-place
   sorting by components can never poison the cache. */
const GET_CACHE_TTL_MS = 25_000;
const GET_CACHE_MAX = 80;
const _getCache = new Map();
const _cacheSkip = (url) => /\/auth\/|\/export|\/download|\/backup/.test(url || "");
const _clone = (x) => (typeof structuredClone === "function" ? structuredClone(x) : JSON.parse(JSON.stringify(x)));

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (method !== "get") {
    _getCache.clear();                       // mutations invalidate all reads
    return config;
  }
  if (config.responseType || _cacheSkip(config.url) || config.noCache) return config;
  // Cache key MUST include axios `params` — they are not part of config.url,
  // so keying on the url alone makes different queries to the same path
  // collide (e.g. /mis/previous-data?section=press served the section=fg
  // response). The key is stamped on config so the response interceptor
  // stores under the exact same key.
  const _key = config.url + (config.params ? "::" + JSON.stringify(config.params) : "");
  config._cacheKey = _key;
  const hit = _getCache.get(_key);
  if (hit && Date.now() - hit.t < GET_CACHE_TTL_MS) {
    config.adapter = () => Promise.resolve({
      data: _clone(hit.data), status: hit.status, statusText: "OK (cache)",
      headers: hit.headers, config, request: null,
    });
  }
  return config;
});

api.interceptors.response.use((response) => {
  const cfg = response.config || {};
  const method = (cfg.method || "get").toLowerCase();
  if (method === "get" && !cfg.responseType && !_cacheSkip(cfg.url) && !cfg.noCache
      && response.status === 200 && response.statusText !== "OK (cache)") {
    try {
      if (_getCache.size >= GET_CACHE_MAX) _getCache.delete(_getCache.keys().next().value);
      _getCache.set(cfg._cacheKey || cfg.url, { t: Date.now(), data: _clone(response.data), status: 200, headers: response.headers });
    } catch { /* non-serialisable payload — just skip caching it */ }
  }
  return response;
});

export default api;
